import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './PrivacyPolicy.css';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_shine-hero/artifacts/u84wdb4s_HHGC%20%20%28Logo%29.png";

const PrivacyPolicy = () => {
  return (
    <div className="privacy-page" data-testid="privacy-policy-page">
      <header className="privacy-header">
        <div className="privacy-header-content">
          <Link to="/" className="back-link">
            <ArrowLeft className="back-icon" />
            <span>Back to Home</span>
          </Link>
          <img src={LOGO_URL} alt="Headlight Hero GC" className="privacy-logo" />
        </div>
      </header>

      <main className="privacy-main">
        <div className="privacy-container">
          <h1 className="privacy-title">PRIVACY POLICY</h1>
          <p className="privacy-updated">Last updated: April 2026</p>
          
          <div className="privacy-intro">
            <p>
              Headlight Hero GC is a Gold Coast-based mobile headlight restoration service. 
              We are committed to protecting your personal information and handling it in accordance with the 
              Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
            </p>
            <p>
              By using our services or contacting us, you agree to the collection and use of information in 
              accordance with this policy.
            </p>
          </div>

          <section className="privacy-section">
            <h2>1. Who We Are</h2>
            <ul>
              <li><strong>Business Name:</strong> Headlight Hero GC</li>
              <li><strong>Location:</strong> Gold Coast, Queensland, Australia</li>
              <li><strong>Contact:</strong> rick@headlightherogc.com.au</li>
              <li><strong>Phone:</strong> +61 416 630 605</li>
              <li><strong>Website:</strong> headlightherogc.com.au</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>2. What Information We Collect</h2>
            <p>We collect only the information necessary to provide our headlight restoration services. This may include:</p>
            
            <h3>Personal Information</h3>
            <ul>
              <li>Your name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Physical address or suburb (for mobile service bookings)</li>
              <li>Vehicle make, model, and year</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <p>If you visit our website, we may automatically collect:</p>
            <ul>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>IP address (non-identifying)</li>
              <li>Referring website</li>
            </ul>
            <p>We do not collect sensitive information such as financial data, health information, or government identifiers.</p>
          </section>

          <section className="privacy-section">
            <h2>3. How We Collect Your Information</h2>
            <p>We collect your personal information in the following ways:</p>
            <ul>
              <li><strong>Directly from you</strong> — when you contact us by phone, email, social media, or through our website enquiry form</li>
              <li><strong>Booking information</strong> — when you book a service appointment</li>
              <li><strong>Social media interactions</strong> — if you message us via Facebook or other platforms</li>
              <li><strong>Referrals</strong> — when someone recommends our service to us on your behalf</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Why We Collect Your Information</h2>
            <p>We collect and use your personal information only for the following purposes:</p>
            <ul>
              <li>To provide you with headlight restoration services</li>
              <li>To confirm and manage your service booking</li>
              <li>To contact you before, during, or after your appointment</li>
              <li>To send you a service quote or invoice</li>
              <li>To respond to your enquiries and questions</li>
              <li>To improve our service quality and customer experience</li>
              <li>To comply with our legal and regulatory obligations</li>
            </ul>
            <p>We will not use your information for any purpose beyond what is described above without first obtaining your consent.</p>
          </section>

          <section className="privacy-section">
            <h2>5. Who We Share Your Information With</h2>
            <p>We do not sell, trade, or rent your personal information to third parties.</p>
            <p>We may share limited information only where necessary, including:</p>
            <ul>
              <li><strong>Service providers</strong> — such as payment platforms or booking software we use to operate our business</li>
              <li><strong>Legal requirements</strong> — if required by law, court order, or a government authority</li>
              <li><strong>Business transfers</strong> — in the unlikely event of a business sale or restructure</li>
            </ul>
            <p>Any third party we work with is required to handle your information in accordance with applicable privacy laws.</p>
          </section>

          <section className="privacy-section">
            <h2>6. How We Store and Protect Your Information</h2>
            <p>Your personal information may be stored:</p>
            <ul>
              <li>In secure cloud-based tools (such as Google Workspace or CRM software)</li>
              <li>On password-protected devices</li>
              <li>In physical records kept securely</li>
            </ul>
            <p>
              We take reasonable steps to protect your information from misuse, interference, loss, and unauthorised access. 
              We do not store payment card details — all payments are processed through secure third-party providers.
            </p>
            <p>When your information is no longer needed, we will delete or de-identify it in a secure manner.</p>
          </section>

          <section className="privacy-section">
            <h2>7. Cookies and Website Tracking</h2>
            <p>
              Our website may use cookies — small text files stored on your device — to improve your browsing experience. 
              Cookies help us understand how visitors use our site so we can improve it.
            </p>
            <p>
              You can control or disable cookies through your browser settings. However, disabling cookies may affect 
              some website functionality.
            </p>
            <p>We do not use cookies to collect personally identifiable information without your knowledge.</p>
          </section>

          <section className="privacy-section">
            <h2>8. Your Rights</h2>
            <p>Under the Australian Privacy Principles, you have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request corrections to any information that is inaccurate, incomplete, or out of date</li>
              <li>Lodge a complaint if you believe your privacy has been breached</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the details in Section 1. 
              We will respond within a reasonable timeframe (typically within 30 days).
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Marketing Communications</h2>
            <p>
              We will only contact you with marketing messages (such as promotional offers or seasonal reminders) 
              if you have given us permission to do so.
            </p>
            <p>You may opt out of marketing communications at any time by:</p>
            <ul>
              <li>Replying "STOP" to any SMS</li>
              <li>Clicking "Unsubscribe" in any email</li>
              <li>Contacting us directly and asking to be removed</li>
            </ul>
            <p>We will not send you unsolicited marketing communications.</p>
          </section>

          <section className="privacy-section">
            <h2>10. Links to Other Websites</h2>
            <p>
              Our website may contain links to third-party websites (such as our Facebook page or review platforms). 
              We are not responsible for the privacy practices of those websites. We encourage you to review the 
              privacy policies of any external sites you visit.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Complaints</h2>
            <p>
              If you believe we have breached your privacy, we want to hear from you first. Please contact us directly 
              and we will do our best to resolve the issue promptly.
            </p>
            <p>If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC):</p>
            <ul>
              <li><strong>Website:</strong> www.oaic.gov.au</li>
              <li><strong>Phone:</strong> 1300 363 992</li>
              <li><strong>Post:</strong> GPO Box 5218, Sydney NSW 2001</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal obligations. 
              When we do, we will update the "Last updated" date at the top of this document.
            </p>
            <p>
              We encourage you to review this policy periodically. Continued use of our services after any changes 
              constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>13. Contact Us</h2>
            <p>For any privacy-related questions, requests, or complaints, please contact us:</p>
            <ul>
              <li><strong>Headlight Hero GC</strong></li>
              <li>Gold Coast, Queensland, Australia</li>
              <li><strong>Email:</strong> rick@headlightherogc.com.au</li>
              <li><strong>Phone:</strong> +61 416 630 605</li>
              <li><strong>Website:</strong> headlightherogc.com.au</li>
            </ul>
          </section>

          <div className="privacy-footer-note">
            <p>
              This Privacy Policy was prepared in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles. 
              It applies to all personal information collected by Headlight Hero GC in the course of its business operations.
            </p>
          </div>
        </div>
      </main>

      <footer className="privacy-page-footer">
        <p>&copy; {new Date().getFullYear()} Headlight Hero GC. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
